// Zone switcher for the Tables tab. Scoping the floor/list to one zone
// hides the others - so the trigger and the dropdown surface each zone's
// LIVE situation (open tabs + a red "needs attention" count for service
// calls / bill requests). If a zone you're NOT viewing needs staff, the
// trigger shows a red badge so you notice without hunting. `null` = "No zone".
import { useT } from '@/i18n';
import { Badge, List, ListItem, Popover, f7 } from 'framework7-react';
import { type MutableRefObject, useRef } from 'react';

export interface ZoneTab {
  id: string | null;
  label: string;
  /** Tables with an open tab in this zone. */
  open: number;
  /** Pending service calls + bill requests in this zone. */
  attention: number;
}

// Matches the F7-react Popover ref shape (mirrors ReservationActions).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

export default function ZonePicker({
  zones,
  activeId,
  onChange,
}: {
  zones: ZoneTab[];
  activeId: string | null;
  onChange: (id: string | null) => void;
}) {
  const t = useT().tableOrdering.zonePicker;
  const popoverRef = useRef<PopoverInstance>(null);
  if (zones.length === 0) return null;

  const current = zones.find((z) => z.id === activeId) ?? zones[0];
  const multi = zones.length > 1;
  // Attention in zones the operator is NOT currently viewing.
  const elsewhere = zones.filter((z) => z.id !== activeId).reduce((n, z) => n + z.attention, 0);

  return (
    <div className="to-zone-bar">
      <button
        type="button"
        className="to-zone-trigger"
        disabled={!multi}
        aria-haspopup="listbox"
        onClick={(e) => {
          if (!multi) return;
          const target = e.currentTarget;
          if (popoverRef.current?.el) f7.popover.open(popoverRef.current.el, target);
        }}
      >
        <i className="f7-icons">placemark</i>
        <span className="to-zone-trigger__val">{current.label}</span>
        {current.open > 0 && (
          <span className="to-zone-trigger__sub">{t.openCount(current.open)}</span>
        )}
        {elsewhere > 0 && (
          <Badge color="red" className="to-zone-trigger__elsewhere">
            {elsewhere}
          </Badge>
        )}
        {multi && <i className="f7-icons to-zone-trigger__chev">chevron_down</i>}
      </button>

      {multi && (
        <Popover
          ref={popoverRef as MutableRefObject<PopoverInstance>}
          className="to-zone-pop"
          style={{ minWidth: 260 }}
        >
          <List dividers>
            {zones.map((z) => {
              const active = z.id === activeId;
              return (
                <ListItem
                  key={z.id ?? '__none'}
                  link="#"
                  noChevron
                  title={z.label}
                  footer={z.open > 0 ? t.openCount(z.open) : t.noOpenTabs}
                  onClick={() => {
                    onChange(z.id);
                    f7.popover.close();
                  }}
                >
                  <div slot="after" className="to-zone-pop__after">
                    {z.attention > 0 && <Badge color="red">{z.attention}</Badge>}
                    {active && (
                      <i
                        className="f7-icons"
                        style={{ color: 'var(--f7-theme-color)', fontSize: 20 }}
                      >
                        checkmark_alt
                      </i>
                    )}
                  </div>
                </ListItem>
              );
            })}
          </List>
        </Popover>
      )}
    </div>
  );
}
